import dayjs, { Dayjs } from 'dayjs';

/**
 * Minimal shape of an existing estimate for cross-record period validation.
 * Dates are `YYYY-MM-DD` strings; a null `endDate` means open-ended (the period
 * covers `startDate` onward). Satisfied by both SubscriptionEstimateType and the
 * grid's SubscriptionEstimateRow.
 */
export interface EstimatePeriod {
  id: number;
  startDate: string;
  endDate: string | null;
}

/** The candidate period being added/edited, straight from the form values. */
export interface EstimatePeriodCandidate {
  startDate: Date | null;
  endDate: Date | null;
}

export interface EstimatePeriodCheck {
  hasOverlap: boolean;
  hasGap: boolean;
}

// Existing periods, normalized to Dayjs. A null end means open-ended (+∞).
type NormalizedPeriod = { start: Dayjs; end: Dayjs | null };

// Inclusive-end range overlap. A null end means open-ended (extends to +∞).
// Contiguous periods (one ends day D, the next starts D+1) do NOT overlap.
const rangesOverlap = (
  aStart: Dayjs,
  aEnd: Dayjs | null,
  bStart: Dayjs,
  bEnd: Dayjs | null,
): boolean => {
  const aStartWithinB = bEnd === null || !aStart.isAfter(bEnd, 'day');
  const bStartWithinA = aEnd === null || !bStart.isAfter(aEnd, 'day');
  return aStartWithinB && bStartWithinA;
};

// Nearest neighbor entirely before the candidate: an open-ended record would
// overlap anything on/after its start, so a non-overlapping "before" neighbor
// always has a finite end. Nearest = the largest such end.
const nearestEndBefore = (
  others: NormalizedPeriod[],
  cStart: Dayjs,
): Dayjs | null =>
  others
    .filter(other => other.end !== null && other.end.isBefore(cStart, 'day'))
    .map(other => other.end as Dayjs)
    .reduce<Dayjs | null>(
      (nearest, end) =>
        !nearest || end.isAfter(nearest, 'day') ? end : nearest,
      null,
    );

// Nearest neighbor entirely after the candidate. Nearest = the smallest such
// start. Only meaningful when the candidate has a finite end (an open-ended
// candidate covers everything onward), which the caller guards.
const nearestStartAfter = (
  others: NormalizedPeriod[],
  cEnd: Dayjs,
): Dayjs | null =>
  others
    .filter(other => other.start.isAfter(cEnd, 'day'))
    .reduce<Dayjs | null>(
      (nearest, other) =>
        !nearest || other.start.isBefore(nearest, 'day')
          ? other.start
          : nearest,
      null,
    );

// Detect uncovered space between the candidate and its nearest neighbors. A gap
// exists when the candidate is more than one day from a neighbor (a one-day
// difference is contiguous). Checks both the nearest neighbor-end before the
// candidate and, when the candidate has a finite end, the nearest neighbor-start
// after it.
const hasGapAgainst = (
  cStart: Dayjs,
  cEnd: Dayjs | null,
  nearestBeforeEnd: Dayjs | null,
  nearestAfterStart: Dayjs | null,
): boolean => {
  if (nearestBeforeEnd && cStart.diff(nearestBeforeEnd, 'day') > 1) {
    return true;
  }

  if (cEnd && nearestAfterStart && nearestAfterStart.diff(cEnd, 'day') > 1) {
    return true;
  }

  return false;
};

/**
 * Cross-record date-range check for a subscription estimate against the OTHER
 * existing records (the record identified by `editingId` is excluded).
 *
 * Semantics match the backend and computeActiveStatus: periods are
 * `[startDate, endDate]` inclusive, and a null `endDate` is open-ended.
 *
 * - `hasOverlap`: candidate shares at least one day with an existing record.
 * - `hasGap`: candidate leaves at least one uncovered day between it and its
 *   nearest neighbor on either side (estimates must form a contiguous chain).
 *
 * Pure: no side effects.
 */
export const checkEstimatePeriod = (
  candidate: EstimatePeriodCandidate,
  existingEstimates: EstimatePeriod[],
  editingId?: number,
): EstimatePeriodCheck => {
  // Start date is required by the per-record schema; without it there is
  // nothing to compare, so report clean and let field validation gate submit.
  if (!candidate.startDate) {
    return { hasOverlap: false, hasGap: false };
  }

  const cStart = dayjs(candidate.startDate);
  const cEnd = candidate.endDate ? dayjs(candidate.endDate) : null;

  const others = existingEstimates
    .filter(estimate => estimate.id !== editingId)
    .map(estimate => ({
      start: dayjs(estimate.startDate),
      end: estimate.endDate ? dayjs(estimate.endDate) : null,
    }));

  const hasOverlap = others.some(other =>
    rangesOverlap(cStart, cEnd, other.start, other.end),
  );

  // Overlap is the reported failure; gap detection is only meaningful when the
  // candidate sits cleanly beside its neighbors.
  if (hasOverlap) {
    return { hasOverlap: true, hasGap: false };
  }

  const nearestBeforeEnd = nearestEndBefore(others, cStart);
  // An open-ended candidate covers everything onward, so it can have no "after"
  // neighbor; only look for one when the candidate has a finite end.
  const nearestAfterStart =
    cEnd === null ? null : nearestStartAfter(others, cEnd);

  return {
    hasOverlap: false,
    hasGap: hasGapAgainst(cStart, cEnd, nearestBeforeEnd, nearestAfterStart),
  };
};
